import { doc, getDoc, increment, collection, query, where, getDocs, setDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { currentProductPrice } from "./currentProductPrice";
import createAnonymousUser from "./createAnonymousUser";
import analyticsHelper from "../utils/analyticsHelper";
import getCartItemPriceData from "./getCartItemPriceData";
import { cleanDataForDb } from "../utils/format";
import isProductAvailable from "./isProductAvailable";

const base62 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

const numberToBase62 = (num, length) => {
  let result = "";
  while (result.length < length) {
    result = base62[num % 36] + result;
    num = Math.floor(num / 36);
  }
  return result;
};

// Pass firestore instance as an argument
const addToCart = async (product_id, userSettings, firestore, quantity = 1, collectionPath, options = {}) => {
  const { overrideQuantity = false } = options;

  try {
    // Check if userSettings contains a valid user or create an anonymous user if necessary
    let uid;
    if (userSettings && userSettings.uid) {
      // If userSettings has a valid user, use its UID
      uid = userSettings.uid;
    } else {
      // Create an anonymous user and get its UID
      const user = await createAnonymousUser();
      uid = user.uid;
    }

    // Fetch data from Firestore using doc and getDoc
    const docRefPath = `${collectionPath}Listings/ListingsList/DataBase/${product_id}`;
    const docRef = doc(firestore, docRefPath);
    const docSnap = await getDoc(docRef);
    const productData = docSnap.data();

    if (!productData) {
      console.log("No such document!");
      window.alert("Product not found!");
      return;
    }

    // Check listing stock amount
    if (!isProductAvailable(productData)) {
      console.log("Product not available!");
      window.alert("Product not available!");
      return;
    }

    // Determine if the product is on sale
    productData.finalPrice = await currentProductPrice(productData);
    const cartRefPath = `${collectionPath}Carts/CartsList/DataBase`;
    const cartRef = query(collection(firestore, cartRefPath), where("user_id", "==", uid), where("status", "==", "ordering"));
    const cartSnap = await getDocs(cartRef);
    let cartID;
    const priceData = await getCartItemPriceData(productData);

    // Data to remove from productData
    const addToCartData = {
      brand_name: productData.brand_name,
      brand_uid: productData.brand_uid,
      listing_sale_endDate: productData?.listing_sale_endDate,
      sooperdeal_expiration_date: productData?.sooperdeal_expiration_date,
      sooperdeal_start_date: productData?.sooperdeal_start_date,
      perishable: productData.perishable,
      product_id: productData.id,
      product_name: productData?.product_name,
      images: productData?.images,
      product_uid: productData.product_uid,
      seller_id: productData.seller_id,
      seller_name: productData.seller_name,
      seller_product_id: productData.seller_product_id,
      ...priceData,
    };

    analyticsHelper.addToCart(addToCartData)

    if (cartSnap.empty) {
      // Create a new cart
      const newCartRef = doc(collection(firestore, cartRefPath));
      cartID = newCartRef.id;
      await setDoc(newCartRef, {
        user_id: uid,
        status: "ordering",
        date: new Date(),
        cart_sooperstock_ID: numberToBase62(Math.floor(Math.random() * 1000000000), 6),
      });

      // Ensure all undefined values in addToCartData are converted to null
      const cartDataWithDefaults = cleanDataForDb(addToCartData);

      // Add item to the new cart
      await setDoc(doc(firestore, `${cartRefPath}/${cartID}/Items`, product_id), {
        ...cartDataWithDefaults,
        quantity,
      });
    } else {
      // Update existing cart
      cartID = cartSnap.docs[0].id;
      const itemRef = doc(firestore, `${cartRefPath}/${cartID}/Items`, product_id);
      const itemSnap = await getDoc(itemRef);
      const currentItemQuantity = itemSnap.exists() ? itemSnap.data().quantity : 0;

      // Handle SooperDeal limit
      const maxAllowedQuantity =
        productData.finalPrice?.type === "sooperdeal" ? productData.sooperdeal_limit_per_order || Infinity : productData.listing_limit_per_order || Infinity;
      const totalQuantity = overrideQuantity ? quantity : currentItemQuantity + quantity;

      if (totalQuantity > maxAllowedQuantity) {
        window.alert(`You can only add a maximum of ${maxAllowedQuantity} units of this item.`);
        return;
      }

      // Ensure all undefined values in addToCartData are converted to null
      const cartDataWithDefaults = cleanDataForDb(addToCartData);

      if (itemSnap.exists()) {
        if (totalQuantity === 0) {
          // Delete the item from the cart if the quantity is zero
          await deleteDoc(itemRef);
        } else {
          await updateDoc(itemRef, {
            quantity: totalQuantity,
          });
        }
      } else if (quantity > 0) {
        // Only add a new item if quantity is greater than zero
        await setDoc(itemRef, {
          ...cartDataWithDefaults,
          quantity,
        });
      }
    }

    // Update the total number of items in the cart
    const cartItemsSnap = await getDocs(collection(firestore, `${cartRefPath}/${cartID}/Items`));
    let totalItems = 0;
    cartItemsSnap.forEach((item) => {
      totalItems += item.data().quantity;
    });

    // You can handle totalItems here as needed for your web app
    console.log(`Total items in cart: ${totalItems}`);
  } catch (error) {
    console.error("Error adding to cart: ", error);
    // Handle error appropriately for your web app
  }
};

export default addToCart;
